import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ShareButton from "@/components/feature/ShareButton";
import usePageMeta from "@/hooks/usePageMeta";
import { getNewsById, categoryMeta } from "@/lib/content";
import { newsList } from "@/mocks/news";
import type { NewsItem } from "@/types/content";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const news = id ? getNewsById(id) : undefined;

  usePageMeta({
    title: news ? `${news.title} - 香蕉动漫` : "该资讯不存在 - 香蕉动漫",
    description: news ? news.summary : undefined,
    keywords: news ? `${news.tags.join(",")},香蕉动漫,动漫资讯` : undefined,
  });

  if (!news) {
    return (
      <div className="w-full px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-file-unknow-line"
          title="该资讯不存在或已删除"
          description="你访问的资讯可能已被删除或链接有误，看看其他最新动漫资讯吧。"
          linkTo="/category/news"
          linkLabel="浏览动漫资讯"
        />
      </div>
    );
  }

  const related = (newsList as NewsItem[])
    .filter((item) => item.id !== news.id)
    .filter((item) => item.tags.some((t) => news.tags.includes(t)))
    .concat((newsList as NewsItem[]).filter((item) => item.id !== news.id))
    .filter((item, index, arr) => arr.findIndex((x) => x.id === item.id) === index)
    .slice(0, 4);

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: categoryMeta.news.title, to: "/category/news" },
          { label: news.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
        <article className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {news.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
              >
                {t}
              </span>
            ))}
          </div>

          <h1 className="font-display font-black text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-snug">
            {news.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              {news.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              {news.source}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-folder-line w-4 h-4 flex items-center justify-center"></i>
              <Link to="/category/news" className="hover:text-accent-600 transition-colors cursor-pointer">
                {categoryMeta.news.title}
              </Link>
            </span>
          </div>

          <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden border border-background-200">
            <img
              src={news.cover}
              alt={news.title}
              title={`${news.title} - 香蕉动漫`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-6 space-y-4">
            {news.content.map((paragraph, index) => (
              <p key={index} className="text-sm md:text-base leading-8 text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-background-200 flex items-center justify-between gap-4">
            <p className="text-xs text-foreground-400">本文为演示内容，正式上线需接入真实资讯接口。</p>
            <ShareButton title={news.title} />
          </div>
        </article>

        <aside className="min-w-0">
          <h2 className="font-display font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-links-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
            相关资讯
          </h2>
          <div className="flex flex-col gap-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/detail/news/${item.id}`}
                className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="shrink-0 w-20 h-16 rounded-md overflow-hidden">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 香蕉动漫`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm text-foreground-900 line-clamp-2 group-hover:text-accent-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">{item.date}</p>
                </div>
              </Link>
            ))}
          </div>

          <Link
            to="/category/news"
            className="mt-5 flex items-center justify-between rounded-lg bg-primary-100 px-4 py-3 text-sm font-medium text-accent-700 hover:bg-primary-200 transition-colors cursor-pointer"
          >
            查看全部动漫资讯
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </aside>
      </div>
    </div>
  );
}